import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller powering the "Route to Survey" drawer.
 *
 * Responsibilities:
 *   - toggle visibility between the "new Survey" and "existing Survey"
 *     panels based on the selected mode radio
 *   - toggle visibility of each question row's options textarea based on
 *     whether the selected question type is a choice type (radio /
 *     checkbox / select)
 *
 * Targets: newPanel, existingPanel, modeRadio, typeSelect, optionsContainer,
 * optionsField, valueMapContainer, valueMapTarget
 */
const CHOICE_TYPES = new Set(["radio", "checkbox", "select"])

export default class SurveyRoutingDrawerController extends Controller<HTMLElement> {
  static targets = [
    "newPanel",
    "existingPanel",
    "modeRadio",
    "typeSelect",
    "optionsContainer",
    "optionsField",
    "valueMapContainer",
    "valueMapTarget",
  ]

  declare readonly hasNewPanelTarget: boolean
  declare readonly newPanelTarget: HTMLElement
  declare readonly hasExistingPanelTarget: boolean
  declare readonly existingPanelTarget: HTMLElement
  declare readonly modeRadioTargets: HTMLInputElement[]
  declare readonly typeSelectTargets: HTMLSelectElement[]
  declare readonly optionsContainerTargets: HTMLElement[]
  declare readonly optionsFieldTargets: HTMLTextAreaElement[]
  declare readonly valueMapContainerTargets: HTMLElement[]
  declare readonly valueMapTargetTargets: HTMLSelectElement[]

  connect(): void {
    this.syncPanels()
    this.typeSelectTargets.forEach((select) => this.syncOptionsField(select))
  }

  modeChanged(): void {
    this.syncPanels()
  }

  typeChanged(event: Event): void {
    const select = event.currentTarget as HTMLSelectElement
    this.syncOptionsField(select)
  }

  optionsChanged(event: Event): void {
    const field = event.currentTarget as HTMLTextAreaElement
    const row = field.closest("[data-survey-routing-drawer-target~='row']")
    if (!row) return

    const options = this.parseOptions(field.value)
    row
      .querySelectorAll<HTMLSelectElement>("[data-survey-routing-drawer-target='valueMapTarget']")
      .forEach((select) => this.syncTargetOptions(select, options))
  }

  private syncPanels(): void {
    const mode = this.selectedMode()
    if (this.hasNewPanelTarget) {
      this.newPanelTarget.classList.toggle("hidden", mode !== "new")
    }
    if (this.hasExistingPanelTarget) {
      this.existingPanelTarget.classList.toggle("hidden", mode !== "existing")
    }
  }

  private selectedMode(): string {
    const checked = this.modeRadioTargets.find((radio) => radio.checked)
    return checked ? checked.value : "new"
  }

  private syncOptionsField(select: HTMLSelectElement): void {
    const row = select.closest("[data-survey-routing-drawer-target~='row']")
    if (!row) return
    const container = row.querySelector<HTMLElement>(
      "[data-survey-routing-drawer-target='optionsContainer']"
    )
    const field = row.querySelector<HTMLTextAreaElement>(
      "[data-survey-routing-drawer-target='optionsField']"
    )
    if (!field) return
    const isChoice = CHOICE_TYPES.has(select.value)
    container?.classList.toggle("hidden", !isChoice)
    field.classList.toggle("hidden", !isChoice)
    row
      .querySelector<HTMLElement>("[data-survey-routing-drawer-target='valueMapContainer']")
      ?.classList.toggle("hidden", !isChoice)
  }

  private parseOptions(raw: string): string[] {
    const delimiter = raw.match(/\r?\n/) ? /\r?\n/ : /,/

    return raw
      .split(delimiter)
      .map((value) => value.trim())
      .filter((value) => value.length > 0)
  }

  private syncTargetOptions(select: HTMLSelectElement, options: string[]): void {
    const selected = select.value
    select.innerHTML = ""
    select.appendChild(new Option("", ""))
    options.forEach((value) => {
      select.appendChild(new Option(value, value, false, value === selected))
    })
    if (selected && !options.includes(selected)) {
      select.value = ""
    }
  }
}
